import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Modal, Form, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { MdModeEdit, MdRestoreFromTrash } from "react-icons/md";
import { db } from "../../firebase";
import log_activity from "../activity/logger";
import { log_micro_activity } from "../activity/microLogger";
import {
  diffPlayerContactAdded,
  diffPlayerContactEdited,
  diffPlayerContactDeleted,
  getEditorName,
  getEditorUID,
} from "../../utils/microActivityDiff";
import { useAuth } from "../../Context/AuthContext";
import {
  EXTRA_RELATION_OPTIONS,
  OTHER_RELATION_VALUE,
  filterFamilyDropdownPlaceholders,
  mergeRelationFromForm,
  splitRelationForForm,
} from "../../utils/keyContactRelations";
import {
  KeyContactPhoneField,
  phoneFingerprintForDirectoryId,
} from "../shared/KeyContactPhoneField";
import styles from "./detail.module.scss";

const PLAYER_CONTACTS_COLLECTION = "player-key-contacts";
const CONTACT_DIRECTORY_COLLECTION = "key-player-contacts-directory";
const FAMILY_DROPDOWN_COLLECTION = "family-dropdown";

const EMPTY_FORM = {
  relationSelect: "",
  relationOther: "",
  fullName: "",
  cell: "",
  email: "",
  notes: "",
};

const normalizeText = (value) => String(value || "").trim();

const makeDirectoryDocId = ({ fullName, cell, email }) => {
  const raw = `${normalizeText(fullName).toLowerCase()}|${phoneFingerprintForDirectoryId(cell)}|${normalizeText(email).toLowerCase()}`;
  const cleaned = raw.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
  return cleaned || `contact_${Date.now()}`;
};

export const KeyPlayerContactsSection = ({ playerID, playerName }) => {
  const { userInfo, getUserInfoData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [directoryContacts, setDirectoryContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedDirectoryContactId, setSelectedDirectoryContactId] = useState("");
  const [selectedDirectoryContactOption, setSelectedDirectoryContactOption] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadContacts = async () => {
    if (!playerID) return;
    setLoading(true);
    try {
      const snap = await db
        .collection(PLAYER_CONTACTS_COLLECTION)
        .where("playerProfile", "==", playerID)
        .get();

      const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      rows.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });
      setContacts(rows);
    } catch (error) {
      console.error("Error loading key player contacts:", error);
      toast.error("Could not load key player contacts");
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyDropdown = async () => {
    try {
      const snap = await db.collection(FAMILY_DROPDOWN_COLLECTION).orderBy("order").get();
      setFamilyMembers(snap.docs.map((doc) => doc.data().value).filter(Boolean));
    } catch (error) {
      console.error("Error loading family dropdown options:", error);
    }
  };

  const loadDirectory = async () => {
    try {
      const snap = await db.collection(CONTACT_DIRECTORY_COLLECTION).get();
      const items = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      items.sort((a, b) => normalizeText(a.fullName).localeCompare(normalizeText(b.fullName)));
      setDirectoryContacts(items);
    } catch (error) {
      console.error("Error loading contact directory:", error);
    }
  };

  useEffect(() => {
    loadContacts();
    loadFamilyDropdown();
    loadDirectory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerID]);

  const directoryByName = useMemo(() => {
    const map = new Map();
    directoryContacts.forEach((item) => {
      const key = normalizeText(item.fullName).toLowerCase();
      if (key && !map.has(key)) map.set(key, item);
    });
    return map;
  }, [directoryContacts]);

  const familyMembersForRelation = useMemo(
    () => filterFamilyDropdownPlaceholders(familyMembers),
    [familyMembers]
  );

  const extraRelationOptionsFiltered = useMemo(
    () => EXTRA_RELATION_OPTIONS.filter((opt) => !familyMembersForRelation.includes(opt)),
    [familyMembersForRelation]
  );

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setSelectedDirectoryContactId("");
    setSelectedDirectoryContactOption([]);
    setForm(EMPTY_FORM);
  };

  const openAddModal = () => {
    setEditingContact(null);
    setSelectedDirectoryContactId("");
    setSelectedDirectoryContactOption([]);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setSelectedDirectoryContactId(contact.directoryContactId || "");
    const selectedOption = contact.directoryContactId
      ? directoryContacts.find((item) => item.id === contact.directoryContactId)
      : null;
    setSelectedDirectoryContactOption(selectedOption ? [selectedOption] : []);
    setForm({
      ...splitRelationForForm(contact.relation, familyMembersForRelation),
      fullName: contact.fullName || "",
      cell: contact.cell || "",
      email: contact.email || "",
      notes: contact.notes || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (contact) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    try {
      await db.collection(PLAYER_CONTACTS_COLLECTION).doc(contact.id).delete();
      toast.success("Contact deleted");
      log_micro_activity({
        playerID,
        playerName: playerName || "Player",
        section: "Player Contact",
        action: "deleted",
        details: diffPlayerContactDeleted(contact),
        editor: getEditorName(userInfo, getUserInfoData),
        editorUID: getEditorUID(userInfo, getUserInfoData),
      });
      await loadContacts();
    } catch (error) {
      console.error("Error deleting key player contact:", error);
      toast.error("Could not delete contact");
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "relationSelect") {
      setForm((prev) => ({
        ...prev,
        relationSelect: value,
        relationOther: value === OTHER_RELATION_VALUE ? prev.relationOther : "",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildDirectoryOptionLabel = (item) => {
    const parts = [];
    if (normalizeText(item.fullName)) parts.push(normalizeText(item.fullName));
    if (normalizeText(item.relation)) parts.push(normalizeText(item.relation));
    if (normalizeText(item.cell)) parts.push(normalizeText(item.cell));
    if (normalizeText(item.email)) parts.push(normalizeText(item.email));
    return parts.join(" | ");
  };

  const handleSelectExistingContact = (selected) => {
    const selectedItem = Array.isArray(selected) && selected.length > 0 ? selected[0] : null;
    const selectedId = selectedItem ? selectedItem.id : "";
    setSelectedDirectoryContactOption(selectedItem ? [selectedItem] : []);
    setSelectedDirectoryContactId(selectedId);
    if (!selectedId) return;
    setForm((prev) => ({
      ...splitRelationForForm(selectedItem.relation || "", familyMembersForRelation),
      fullName: selectedItem.fullName || prev.fullName || "",
      cell: selectedItem.cell || prev.cell || "",
      email: selectedItem.email || prev.email || "",
      notes: selectedItem.notes || prev.notes || "",
    }));
  };

  const handleClearSelectedExistingContact = () => {
    setSelectedDirectoryContactId("");
    setSelectedDirectoryContactOption([]);
    setForm(EMPTY_FORM);
  };

  const maybeAutofillByName = () => {
    const nameKey = normalizeText(form.fullName).toLowerCase();
    if (!nameKey) return;
    const match = directoryByName.get(nameKey);
    if (!match) return;

    setForm((prev) => {
      const existingRel = mergeRelationFromForm(prev.relationSelect, prev.relationOther);
      const rel = existingRel || match.relation || "";
      return {
        ...splitRelationForForm(rel, familyMembersForRelation),
        fullName: prev.fullName,
        cell: prev.cell || match.cell || "",
        email: prev.email || match.email || "",
        notes: prev.notes || match.notes || "",
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!playerID) return;

    const fullName = normalizeText(form.fullName);
    if (!fullName) {
      toast.error("Full name is required");
      return;
    }

    const relation = mergeRelationFromForm(form.relationSelect, form.relationOther);
    if (form.relationSelect === OTHER_RELATION_VALUE && !normalizeText(form.relationOther)) {
      toast.error("Enter how this contact relates to the player (or pick a preset above)");
      return;
    }

    const payload = {
      relation,
      fullName,
      cell: normalizeText(form.cell),
      email: normalizeText(form.email),
      notes: normalizeText(form.notes),
      playerProfile: playerID,
      playerName: normalizeText(playerName),
      updatedAt: new Date(),
    };

    try {
      const cellFp = phoneFingerprintForDirectoryId(payload.cell);
      let directoryDocId = makeDirectoryDocId(payload);
      if (editingContact?.directoryContactId) {
        const prevFp = phoneFingerprintForDirectoryId(editingContact.cell);
        if (prevFp && prevFp === cellFp) {
          directoryDocId = editingContact.directoryContactId;
        }
      } else if (selectedDirectoryContactId) {
        const dirRow = directoryContacts.find((d) => d.id === selectedDirectoryContactId);
        if (dirRow && phoneFingerprintForDirectoryId(dirRow.cell) === cellFp) {
          directoryDocId = selectedDirectoryContactId;
        }
      }

      await db.collection(CONTACT_DIRECTORY_COLLECTION).doc(directoryDocId).set(
        {
          relation: payload.relation,
          fullName: payload.fullName,
          fullNameLower: payload.fullName.toLowerCase(),
          cell: payload.cell,
          email: payload.email,
          notes: payload.notes,
          updatedAt: new Date(),
          createdAt: editingContact?.directoryCreatedAt || new Date(),
        },
        { merge: true }
      );

      const playerPayload = {
        ...payload,
        directoryContactId: directoryDocId,
      };

      if (editingContact?.id) {
        await db.collection(PLAYER_CONTACTS_COLLECTION).doc(editingContact.id).update(playerPayload);
        toast.success("Contact updated");
        const currentUserInfo = userInfo || getUserInfoData();
        log_activity(
          playerName || "Player",
          "Player Contact Edited",
          currentUserInfo ? `${currentUserInfo.firstName} ${currentUserInfo.lastName}` : "Unknown User",
          playerID || null
        );
        log_micro_activity({
          playerID,
          playerName: playerName || "Player",
          section: "Player Contact",
          action: "edited",
          details: diffPlayerContactEdited(editingContact, payload),
          editor: getEditorName(userInfo, getUserInfoData),
          editorUID: getEditorUID(userInfo, getUserInfoData),
        });
      } else {
        await db.collection(PLAYER_CONTACTS_COLLECTION).add({
          ...playerPayload,
          createdAt: new Date(),
        });
        toast.success("Contact added");
        const currentUserInfo = userInfo || getUserInfoData();
        log_activity(
          playerName || "Player",
          "Player Contact Added",
          currentUserInfo ? `${currentUserInfo.firstName} ${currentUserInfo.lastName}` : "Unknown User",
          playerID || null
        );
        log_micro_activity({
          playerID,
          playerName: playerName || "Player",
          section: "Player Contact",
          action: "added",
          details: diffPlayerContactAdded(payload),
          editor: getEditorName(userInfo, getUserInfoData),
          editorUID: getEditorUID(userInfo, getUserInfoData),
        });
      }

      await Promise.all([loadContacts(), loadDirectory()]);
      closeModal();
    } catch (error) {
      console.error("Error saving key player contact:", error);
      toast.error("Could not save contact");
    }
  };

  return (
    <>
      <div className={styles.feed}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2>Key Contacts</h2>
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={`btn btn-dark ${styles.addMeasurementButton}`}
              onClick={openAddModal}
            >
              Add New Contact
            </button>
          </div>
        </div>

        {loading ? (
          <div className={styles.measurementsEmptyState}>
            <p>Loading contacts...</p>
          </div>
        ) : contacts.length === 0 ? (
          <div className={styles.measurementsEmptyState}>
            <p>No contacts added yet.</p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.keyContactsTable}>
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Full Name</th>
                  <th>Cell</th>
                  <th>Email</th>
                  <th>Notes</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td>{contact.relation || "--"}</td>
                    <td>{contact.fullName || "--"}</td>
                    <td>{contact.cell || "--"}</td>
                    <td>{contact.email || "--"}</td>
                    <td className={styles.keyContactsNotesCell}>{contact.notes || "--"}</td>
                    <td>
                      <div className={styles.actionIcon}>
                        <MdModeEdit onClick={() => handleEdit(contact)} style={{ color: "green" }} />
                        <MdRestoreFromTrash onClick={() => handleDelete(contact)} style={{ color: "red" }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingContact ? "Edit Contact" : "Add New Contact"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Contact</Form.Label>
              <Form.Select name="relationSelect" value={form.relationSelect} onChange={handleFieldChange}>
                <option value="">Select Member</option>
                {familyMembersForRelation.map((member) => (
                  <option key={member} value={member}>
                    {member}
                  </option>
                ))}
                {extraRelationOptionsFiltered.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                <option value={OTHER_RELATION_VALUE}>Other</option>
              </Form.Select>
              {form.relationSelect === OTHER_RELATION_VALUE && (
                <Form.Control
                  className="mt-2"
                  type="text"
                  name="relationOther"
                  value={form.relationOther}
                  onChange={handleFieldChange}
                  placeholder="Describe relationship to the player"
                />
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Use Existing Contact (Optional)</Form.Label>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <Typeahead
                    id="existing-contact-typeahead"
                    labelKey={(option) => buildDirectoryOptionLabel(option)}
                    options={directoryContacts}
                    selected={selectedDirectoryContactOption}
                    onChange={handleSelectExistingContact}
                    placeholder="Search by full name, phone, or email"
                    minLength={0}
                    filterBy={(option, props) => {
                      const text = (props.text || "").toLowerCase().trim();
                      if (!text) return true;
                      return (
                        normalizeText(option.fullName).toLowerCase().includes(text) ||
                        normalizeText(option.cell).toLowerCase().includes(text) ||
                        normalizeText(option.email).toLowerCase().includes(text)
                      );
                    }}
                    renderMenuItemChildren={(option) => (
                      <div>
                        <div style={{ fontWeight: 700 }}>{normalizeText(option.fullName) || "Unnamed Contact"}</div>
                        <div style={{ fontSize: 12, color: "#6c757d" }}>
                          {[
                            normalizeText(option.relation),
                            normalizeText(option.cell),
                            normalizeText(option.email),
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </div>
                      </div>
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleClearSelectedExistingContact}
                >
                  Clear
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleFieldChange}
                onBlur={maybeAutofillByName}
                required
                placeholder="e.g. John Doe"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cell</Form.Label>
              <KeyContactPhoneField
                value={form.cell}
                onChange={(formatted) => setForm((prev) => ({ ...prev, cell: formatted }))}
                inputProps={{ id: "key-contact-cell", "aria-label": "Cell phone" }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={form.email}
                onChange={handleFieldChange}
                placeholder="e.g. contact@email.com"
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={form.notes}
                onChange={handleFieldChange}
                placeholder="Add context or communication notes"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};
